import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  Loader2, Search, Download, DollarSign, Zap, Users, 
  BarChart3, Filter, Gift, RefreshCw, TrendingUp
} from "lucide-react";
import { getApiBaseUrl } from '@/config/api';

const API = getApiBaseUrl();

const USAGE_TYPES = [
  { value: "all", label: "All Types" },
  { value: "animation_generation", label: "Animation Generation" },
  { value: "lipsync", label: "Lip Sync" },
  { value: "ai_analysis", label: "AI Analysis" },
  { value: "pose_detection", label: "Pose Detection" },
  { value: "s3_storage", label: "S3 Storage" },
  { value: "s3_bandwidth", label: "S3 Bandwidth" }
];

export default function UsageReport() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    user_id: "",
    start_date: "",
    end_date: "",
    usage_type: ""
  });
  
  // Grant sparks modal
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [grantData, setGrantData] = useState({
    user_id: "",
    sparks: 1000,
    reason: "",
    is_recurring: false
  });
  const [granting, setGranting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [reportRes, usersRes] = await Promise.all([
        fetchReport(),
        axios.get(`${API}/api/billing/admin/all-users-sparks`, { headers: getAuthHeaders() })
      ]);
      setAllUsers(usersRes.data.users || []);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load usage report");
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      if (filters.usage_type) params.append("usage_type", filters.usage_type);
      
      const response = await axios.get(
        `${API}/api/billing/admin/usage-report?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setReport(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      await fetchReport();
    } catch (error) {
      toast.error("Failed to apply filters");
    } finally {
      setLoading(false);
    }
  };

  const handleGrantSparks = async () => {
    if (!grantData.user_id || grantData.sparks <= 0) {
      toast.error("Please select a user and enter a valid amount");
      return;
    }
    
    setGranting(true);
    try {
      const params = new URLSearchParams({
        target_user_id: grantData.user_id,
        sparks: grantData.sparks.toString(),
        is_recurring: grantData.is_recurring.toString()
      });
      if (grantData.reason) params.append("reason", grantData.reason);
      
      await axios.post(
        `${API}/api/billing/admin/grant-sparks?${params.toString()}`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(`Granted ${grantData.sparks.toLocaleString()} sparks successfully!`);
      setShowGrantModal(false);
      setGrantData({ user_id: "", sparks: 1000, reason: "", is_recurring: false });
      fetchData(); // Refresh
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant sparks");
    } finally {
      setGranting(false);
    }
  };

  const exportReport = () => {
    if (!report) return;
    
    // Create CSV
    let csv = "User Email,User Name,Sparks Used,Real Cost (USD)\n";
    report.by_user.forEach(user => {
      csv += `${user.email},${user.name || ""},${user.sparks_used},${user.real_cost}\n`;
    });
    
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `usage-report-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading && !report) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Sparks Used</p>
                <p className="text-3xl font-bold text-white">
                  {report?.total_sparks_used?.toLocaleString() || 0}
                </p>
              </div>
              <Zap className="w-10 h-10 text-indigo-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-green-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Total Real Cost</p>
                <p className="text-3xl font-bold text-white">
                  ${report?.total_real_cost?.toFixed(2) || "0.00"}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-green-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-zinc-400 text-sm">Active Users</p>
                <p className="text-3xl font-bold text-white">
                  {report?.by_user?.length || 0}
                </p>
              </div>
              <Users className="w-10 h-10 text-amber-400 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-4">
          <CardTitle className="text-white flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">User</Label>
              <Select 
                value={filters.user_id || "all"} 
                onValueChange={(v) => handleFilterChange("user_id", v === "all" ? "" : v)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all">All Users</SelectItem>
                  {allUsers.map(user => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Start Date</Label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange("start_date", e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">End Date</Label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange("end_date", e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">Usage Type</Label>
              <Select 
                value={filters.usage_type || "all"} 
                onValueChange={(v) => handleFilterChange("usage_type", v === "all" ? "" : v)}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {USAGE_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button 
              onClick={handleApplyFilters}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Apply Filters
            </Button>
            <Button 
              variant="outline" 
              onClick={exportReport}
              className="border-zinc-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowGrantModal(true)}
              className="border-green-500/30 text-green-400 hover:bg-green-500/10"
            >
              <Gift className="w-4 h-4 mr-2" />
              Grant Sparks
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Usage by User */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Usage by User
          </CardTitle>
          <CardDescription className="text-zinc-500">
            Detailed breakdown of spark usage and real costs per user
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800">
                <TableHead className="text-zinc-400">User</TableHead>
                <TableHead className="text-zinc-400">Balance</TableHead>
                <TableHead className="text-zinc-400">Plan</TableHead>
                <TableHead className="text-zinc-400 text-right">Sparks Used</TableHead>
                <TableHead className="text-zinc-400 text-right">Real Cost</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allUsers.map((user) => {
                const usage = report?.by_user?.find(u => u.user_id === user.user_id);
                return (
                  <TableRow key={user.user_id} className="border-zinc-800">
                    <TableCell>
                      <div>
                        <p className="text-white font-medium">{user.name || "—"}</p>
                        <p className="text-zinc-500 text-sm">{user.email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={user.spark_balance > 1000 
                          ? "bg-green-500/10 text-green-400 border-green-500/30"
                          : "bg-amber-500/10 text-amber-400 border-amber-500/30"
                        }
                      >
                        <Zap className="w-3 h-3 mr-1" />
                        {user.spark_balance.toLocaleString()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-zinc-700/50 text-zinc-400 border-zinc-600">
                        {user.subscription_plan}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right text-white">
                      {(usage?.sparks_used || 0).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right text-green-400">
                      ${(usage?.real_cost || 0).toFixed(4)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {allUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-zinc-500 py-8">
                    No users found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Usage by Model/Type */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Usage by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(report?.by_usage_type || {}).map(([type, data]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium capitalize">{type.replace(/_/g, " ")}</p>
                    <p className="text-zinc-500 text-sm">{data.count} operations</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{data.sparks_used.toLocaleString()} sparks</p>
                    <p className="text-green-400 text-sm">${data.real_cost.toFixed(2)}</p>
                  </div>
                </div>
              ))}
              {Object.keys(report?.by_usage_type || {}).length === 0 && (
                <p className="text-zinc-500 text-center py-4">No usage data</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader>
            <CardTitle className="text-white text-lg">Usage by Model</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(report?.by_model || {}).map(([model, data]) => (
                <div key={model} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">{model}</p>
                    <p className="text-zinc-500 text-sm">{data.count} calls</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{data.sparks_used.toLocaleString()} sparks</p>
                    <p className="text-green-400 text-sm">${data.real_cost.toFixed(2)}</p>
                  </div>
                </div>
              ))}
              {Object.keys(report?.by_model || {}).length === 0 && (
                <p className="text-zinc-500 text-center py-4">No model data</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grant Sparks Modal */}
      <Dialog open={showGrantModal} onOpenChange={setShowGrantModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Gift className="w-5 h-5 text-green-400" />
              Grant Free Sparks
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Grant sparks to a user. This can be one-time or recurring monthly.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Select User</Label>
              <Select 
                value={grantData.user_id} 
                onValueChange={(v) => setGrantData(prev => ({ ...prev, user_id: v }))}
              >
                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {allUsers.map(user => (
                    <SelectItem key={user.user_id} value={user.user_id}>
                      {user.email} ({user.spark_balance.toLocaleString()} sparks)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Sparks Amount</Label>
              <Input
                type="number"
                value={grantData.sparks}
                onChange={(e) => setGrantData(prev => ({ ...prev, sparks: parseInt(e.target.value) || 0 }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                min={1}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Reason (Optional)</Label>
              <Input
                value={grantData.reason}
                onChange={(e) => setGrantData(prev => ({ ...prev, reason: e.target.value }))}
                className="bg-zinc-800 border-zinc-700 text-white"
                placeholder="e.g., Beta tester reward"
              />
            </div>

            <div 
              className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                grantData.is_recurring ? 'bg-green-500/10 border border-green-500/30' : 'bg-zinc-800/50 border border-transparent'
              }`}
              onClick={() => setGrantData(prev => ({ ...prev, is_recurring: !prev.is_recurring }))}
            >
              <div>
                <p className="text-white font-medium flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Recurring Monthly
                </p>
                <p className="text-zinc-500 text-sm">Auto-grant these sparks every month</p>
              </div>
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                grantData.is_recurring ? 'bg-green-600 border-green-600' : 'border-zinc-600'
              }`}>
                {grantData.is_recurring && <span className="text-white text-xs">✓</span>}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowGrantModal(false)}
              className="text-zinc-400"
            >
              Cancel
            </Button>
            <Button
              onClick={handleGrantSparks}
              disabled={granting || !grantData.user_id}
              className="bg-green-600 hover:bg-green-700"
            >
              {granting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Granting...
                </>
              ) : (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Grant {grantData.sparks.toLocaleString()} Sparks
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
